import { loadGoogleMaps } from "../utils/load_google_maps"
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["map"]
    static values = {
        center: Object,
        markers: Array
    }

    connect() {
        loadGoogleMaps().catch(() => {})
        this.handleMapsReady = () => this.initializeMap()
        window.addEventListener("google-maps:ready", this.handleMapsReady)
        if (window.google?.maps?.Map){
            this.initializeMap()
        }
    }

    initializeMap() {
        if (this.mapInitialized || !window.google?.maps?.Map || window.googleMapsAuthFailed) return
        this.mapInitialized = true
        this.map()
        this.renderMarkers()
        this.observeResize()
        this.queueInitialResize()
    }

    disconnect() {
        window.removeEventListener("google-maps:ready", this.handleMapsReady)
        this.resizeObserver?.disconnect()
        this.resizeObserver = null
        this.mapInitialized = false
        clearTimeout(this.initialResizeTimeout)
        this._infoWindow?.close()
    }

    map() {
        if(this._map === undefined) {
            this._map = new google.maps.Map(this.mapTarget, {
                center: new google.maps.LatLng(
                    this.centerValue.latitude,
                    this.centerValue.longitude
                ),
                zoom: 17
            })
        }
        return this._map
    }

    observeResize() {
        if (this.resizeObserver || typeof ResizeObserver === "undefined") {
            return
        }

        this.resizeObserver = new ResizeObserver(() => {
            if (!this._map) {
                return
            }

            google.maps.event.trigger(this.map(), "resize")
            this.afterResize()
        })

        this.resizeObserver.observe(this.mapTarget)
    }

    queueInitialResize() {
        clearTimeout(this.initialResizeTimeout)

        this.initialResizeTimeout = window.setTimeout(() => {
            if (!this._map) {
                return
            }

            google.maps.event.trigger(this.map(), "resize")
            this.afterResize()
        }, 120)
    }

    markers() {
        if (this._markers === undefined) {
            this._markers = []
        }
        return this._markers
    }

    infoWindow() {
        if (this._infoWindow === undefined) {
            this._infoWindow = new google.maps.InfoWindow({
                headerDisabled: true
            })
        }
        return this._infoWindow
    }

    renderMarkers() {
        this.replaceMarkers(this.markersValue, { fitBounds: true, center: this.centerValue })
    }

    applyMarkerUpdate(markers, center = this.centerValue) {
        this.replaceMarkers(markers, { fitBounds: false, center: center })
    }

    replaceMarkers(markersData, { fitBounds = true, center = this.centerValue } = {}) {
        this.infoWindow()?.close()
        this.markers().forEach((marker) => marker.setMap(null))
        this._markers = []
        this.markerLookup = new Map()

        if (!markersData || markersData.length === 0) {
            if (center) {
                this.map().setCenter({
                    lat: parseFloat(center.latitude),
                    lng: parseFloat(center.longitude)
                })
            }
            return
        }

        const bounds = new google.maps.LatLngBounds()

        markersData.forEach((markerData) => {
            const position = {
                lat: parseFloat(markerData.latitude),
                lng: parseFloat(markerData.longitude)
            }

            const marker = new google.maps.Marker({
                map: this.map(),
                position: position,
                title: markerData.title,
                ...this.markerOptionsFor(markerData)
            })

            if (markerData.title || markerData.address || markerData.run_name) {
                marker.addListener("click", () => {
                    this.openMarker(marker, markerData)
                })
            }

            if (markerData.list_item_id) {
                this.markerLookup.set(markerData.list_item_id, { marker, markerData })
            }

            this.markers().push(marker)
            bounds.extend(position)
        })

        if (!fitBounds) {
            return
        }

        if (markersData.length === 1) {
            this.map().setCenter(bounds.getCenter())
            this.map().setZoom(15)
        } else {
            this.map().fitBounds(bounds, 48)
        }
    }

    afterResize() {
        if (this.hasMarkersValue && this.markersValue.length > 0) {
            this.renderMarkers()
        } else if (this.hasCenterValue) {
            this.map().setCenter({
                lat: parseFloat(this.centerValue.latitude),
                lng: parseFloat(this.centerValue.longitude)
            })
        }
    }

    buildInfoWindowContent(markerData) {
        const wrapper = document.createElement("div")
        wrapper.className = "d-flex flex-column gap-2"
        wrapper.style.maxWidth = "18rem"
        wrapper.style.minWidth = "15rem"

        const header = document.createElement("div")
        header.className = "d-flex align-items-start justify-content-between gap-2"

        const details = document.createElement("div")
        details.className = "d-flex flex-column gap-2 flex-grow-1 min-w-0"

        if (markerData.title) {
            const title = document.createElement("div")
            title.className = "fw-semibold lh-sm"
            title.textContent = markerData.title
            details.appendChild(title)
        }

        if (markerData.address) {
            const address = document.createElement("div")
            address.className = "small fg-2 lh-sm"
            address.textContent = markerData.address
            details.appendChild(address)
        }

        if (markerData.run_name) {
            const runBadge = document.createElement("span")
            runBadge.className = "badge rounded-pill align-self-start"
            runBadge.textContent = markerData.run_name
            runBadge.style.backgroundColor = markerData.color || "#6c757d"
            runBadge.style.color = "#ffffff"
            details.appendChild(runBadge)
        }

        const closeButton = document.createElement("button")
        closeButton.type = "button"
        closeButton.className = "btn btn-sm btn-link fg-2 p-0 lh-1 text-decoration-none flex-shrink-0"
        closeButton.setAttribute("aria-label", "Close")
        closeButton.innerHTML = "&times;"
        closeButton.addEventListener("click", () => {
            this.infoWindow().close()
        })

        header.appendChild(details)
        header.appendChild(closeButton)
        wrapper.appendChild(header)

        const assignmentOptions = this.assignmentOptionsFor(markerData)

        if (assignmentOptions.length) {
            const actions = document.createElement("div")
            actions.className = "border rounded-7 bg-subtle-light p-2 d-flex flex-column gap-2"

            const label = document.createElement("label")
            label.className = "form-label mb-0 fw-semibold small"
            label.textContent = "Assign run"
            actions.appendChild(label)

            const helper = document.createElement("div")
            helper.className = "small fg-2"
            helper.textContent = "Change the assigned run without leaving the map."
            actions.appendChild(helper)

            const select = document.createElement("select")
            select.className = "form-control form-control-sm"

            const placeholder = document.createElement("option")
            placeholder.value = ""
            placeholder.selected = true
            placeholder.textContent = "Select a run"
            select.appendChild(placeholder)

            assignmentOptions.forEach((optionData) => {
                const option = document.createElement("option")
                option.value = optionData.url
                option.textContent = optionData.label
                select.appendChild(option)
            })

            select.addEventListener("change", async (event) => {
                const url = event.target.value
                if (!url) {
                    return
                }

                event.target.disabled = true
                await this.submitMapAssignment(url)
                this.infoWindow().close()
            })

            actions.appendChild(select)
            wrapper.appendChild(actions)
        }

        return wrapper
    }

    openMarker(marker, markerData) {
        const infoWindow = this.infoWindow()
        infoWindow.close()
        infoWindow.setContent(this.buildInfoWindowContent(markerData))
        infoWindow.open(this.map(), marker)
        this.markerClicked(markerData, marker, infoWindow)
    }

    assignmentOptionsFor(markerData) {
        return markerData.assignment_options || []
    }

    async submitMapAssignment(url) {
        const response = await fetch(url, {
            headers: {
                "Accept": "text/vnd.turbo-stream.html",
                "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
            },
            method: "POST"
        })

        const html = await response.text()
        window.Turbo?.renderStreamMessage(html)
    }

    markerOptionsFor(markerData) {
        return { icon: this.buildMarkerIcon(markerData.color) }
    }

    buildMarkerIcon(color = "#6c757d") {
        const svg = `
          <svg xmlns="http://www.w3.org/2000/svg" width="34" height="44" viewBox="0 0 34 44">
            <path fill="${color}" stroke="#ffffff" stroke-width="2" d="M17 1C8.163 1 1 8.163 1 17c0 11.333 13.278 23.114 15.047 24.621a1.47 1.47 0 0 0 1.906 0C19.722 40.114 33 28.333 33 17 33 8.163 25.837 1 17 1Z"/>
            <circle cx="17" cy="17" r="6.5" fill="#ffffff"/>
          </svg>
        `.trim()

        return {
            anchor: new google.maps.Point(17, 42),
            scaledSize: new google.maps.Size(34, 44),
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`
        }
    }

    markerClicked(_markerData, _marker, _infoWindow) {
    }

    // marker() {
    //     if (this._marker == undefined) {
    //         this._marker = new google.maps.Marker({
    //             map: this.map(),
    //             anchorPoint: new google.maps.Point(0,0)
    //         })
    //         let mapLocation = {
    //             lat: parseFloat(this.latitudeTarget.value),
    //             lng: parseFloat(this.longitudeTarget.value)
    //         }
    //         this._marker.setPosition(mapLocation)
    //         this._marker.setVisible(true)
    //     }
    //     return this._marker
    // }

    // autocomplete() {
    //     if (this._autocomplete == undefined) {
    //         this._autocomplete = new google.maps.places.Autocomplete(this.fieldTarget)
    //         this._autocomplete.bindTo('bounds', this.map())
    //         this._autocomplete.setFields(['address_components', 'geometry', 'icon', 'name'])
    //         this._autocomplete.addListener('place_changed', this.locationChanged.bind(this))
    //     }
    //     return this._autocomplete
    // }

    // locationChanged() {
    //     let place = this.autocomplete().getPlace()
    //
    //     if (!place.geometry) {
    //         // User entered the name of a Place that was not suggested and
    //         // pressed the Enter key, or the Place Details request failed.
    //         window.alert("No details available for input: '" + place.name + "'");
    //         return;
    //     }
    //
    //     this.map().fitBounds(place.geometry.viewport)
    //     this.map().setCenter(place.geometry.location)
    //     this.marker().setPosition(place.geometry.location)
    //     this.marker().setVisible(true)
    //
    //     this.latitudeTarget.value = place.geometry.location.lat()
    //     this.longitudeTarget.value = place.geometry.location.lng()
    // }
    //
    // preventSubmit(e) {
    //     if (e.key === "Enter") { e.preventDefault() }
    // }
}
